import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";

const URL = "https://palstars.com/decks/grizzbolt-aggro-build";
const TITLE = "Grizzbolt Aggro Deck Build - Palworld TCG Meta Strategy";
const DESCRIPTION =
  "Competitive Grizzbolt TSP aggro deck list for the Palworld Official Card Game: curve, key printings, play pattern, matchups and what the build costs to assemble.";

const CURVE: Array<[string, string, string]> = [
  ["◇1–◇2", "Early pressure Pals", "Open the board on turn one and force blocks before your opponent sets up."],
  ["◇3–◇4", "Grizzbolt TSP and mid-curve threats", "The build's ceiling: high power with a strike count that closes games."],
  ["◇5+", "Two finishers only", "Aggro loses to its own top end — keep expensive cards to a minimum."],
  ["Support", "Counter and Soul cards", "Protect the curve rather than adding more raw power."],
];

const FAQ: FaqItem[] = [
  {
    q: "Is Grizzbolt aggro good in the Palworld TCG meta?",
    a: "Grizzbolt sits in the aggro shell because its power-to-cost ratio lets you trade up on the mid curve while still applying pressure from turn one. It punishes slow control openings and struggles against decks that stabilise with cheap blockers.",
  },
  {
    q: "How many copies of Grizzbolt TSP do I need?",
    a: "The TSP printing is the chase version, not a requirement — the base printing plays identically. Build with base copies first and upgrade to TSP for collection value once the shell is finished.",
  },
  {
    q: "What beats Grizzbolt aggro?",
    a: "Decks with cheap high-power blockers and card advantage engines. If your opening two turns do not connect, the aggro plan runs out of resources before the finishers land.",
  },
  {
    q: "What does the Grizzbolt aggro deck cost to build?",
    a: "Almost all of the cost sits in the chase printings. With base printings the shell is inexpensive; swapping in TSP alternate art multiplies the total, which is why the pricing ledger matters before you buy.",
  },
];

export const Route = createFileRoute("/decks/grizzbolt-aggro-build")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Deck Builder", "/deck-builder"],
        ["Grizzbolt Aggro Build", "/decks/grizzbolt-aggro-build"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: GrizzboltAggroPage,
});

function GrizzboltAggroPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · deck strategy</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Competitive Grizzbolt TSP Aggro Deck List Guide</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Grizzbolt aggro wins by curving out: cheap pressure on turns one and two, Grizzbolt on the mid curve to trade
          up, and no more than two expensive finishers so the deck never stalls on an unplayable hand. The TSP printing
          is a collection upgrade, not a power upgrade — the base card plays the same.
        </p>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
          Before committing to the chase copies, cross-reference your values against the updated{" "}
          <Link to="/tcg-price-index" className="text-primary hover:underline">
            Palworld TCG price index market trends
          </Link>{" "}
          ledger so you are not buying into a spike.
        </p>
      </header>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">The curve</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <caption className="sr-only">Grizzbolt aggro deck curve by cost band</caption>
            <thead>
              <tr className="border-b border-border/60 text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-2 pr-4">Cost band</th>
                <th scope="col" className="py-2 pr-4">Role</th>
                <th scope="col" className="py-2">Why it is there</th>
              </tr>
            </thead>
            <tbody>
              {CURVE.map(([band, role, why]) => (
                <tr key={band} className="border-b border-border/40">
                  <td className="py-2 pr-4 font-semibold">{band}</td>
                  <td className="py-2 pr-4">{role}</td>
                  <td className="py-2 text-muted-foreground">{why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play it</h2>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>1. Commit early. A turn-one board that forces a block is worth more than holding a perfect curve.</li>
          <li>2. Trade up with Grizzbolt, never sideways — spend it on the opposing threat that blanks your attackers.</li>
          <li>3. Hold Counter cards for the stabilising turn, not for tempo.</li>
          <li>4. If the game reaches the late turns even, switch to attrition and win on the last finisher.</li>
        </ol>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Cost the build before you buy</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Chase printings decide what this deck costs, so price it against completed sales rather than asking listings.
          Check the{" "}
          <Link to="/tcg-sold-prices" className="text-primary hover:underline">
            Palworld TCG last sold prices index
          </Link>{" "}
          before ordering the TSP copies.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          <li>
            <Link to="/deck-builder" className="text-primary hover:underline">
              Palworld TCG deck builder
            </Link>{" "}
            — build and save the list card by card.
          </li>
          <li>
            <Link to="/tcg-bulk-order" className="text-primary hover:underline">
              Palworld TCG bulk order
            </Link>{" "}
            — paste the card codes and order the whole shell at once.
          </li>
          <li>
            <Link to="/palworld-card-value" className="text-primary hover:underline">
              Palworld card value pages
            </Link>{" "}
            — per-printing valuation for the chase upgrades.
          </li>
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          If you are tracking large duplicate lots from the shell, calculate total worth using the unified{" "}
          <Link to="/marketplace" className="text-primary hover:underline">
            Palworld TCG bulk orders platform
          </Link>{" "}
          appraiser tool.
        </p>
      </section>

      <FaqSection items={FAQ} heading="Grizzbolt aggro deck FAQ" />
    </div>
  );
}
